import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { ListSkeleton } from "@/shared/components";
import { CheckCircle2, XCircle, Loader2, Circle } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const StepProgressSchema = z.object({
  steps: z.array(
    z.object({
      label: z.string(),
      status: z.enum(["pending", "running", "done", "error"]),
      detail: z.string().optional(),
    }),
  ),
});

type StepProgressData = z.infer<typeof StepProgressSchema>;

/* ────────────────────── Status config ────────────────────── */

const STATUS_CONFIG = {
  pending: {
    icon: <Circle className="size-4 text-text-tertiary/40" />,
    labelColor: "text-text-tertiary",
    detailColor: "text-text-tertiary/60",
    bg: "",
  },
  running: {
    icon: <Loader2 className="size-4 animate-spin text-text-brand" />,
    labelColor: "text-text-primary font-medium",
    detailColor: "text-text-tertiary",
    bg: "bg-accent/5",
  },
  done: {
    icon: <CheckCircle2 className="size-4 text-emerald-600" />,
    labelColor: "text-text-primary",
    detailColor: "text-text-tertiary",
    bg: "",
  },
  error: {
    icon: <XCircle className="size-4 text-red-500" />,
    labelColor: "text-red-600 font-medium",
    detailColor: "text-red-400",
    bg: "bg-red-50/50",
  },
} as const;

/* ────────────────────── Component ────────────────────── */

export function StepProgress({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<StepProgressData>) {
  const { steps } = widget.data;

  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      {steps.map((step, idx) => {
        const cfg = STATUS_CONFIG[step.status];
        const isLast = idx === steps.length - 1;

        return (
          <div key={idx} className="flex gap-3">
            {/* Icon + connecting line */}
            <div className="flex flex-col items-center">
              <div className="flex size-6 shrink-0 items-center justify-center">
                {cfg.icon}
              </div>
              {!isLast && (
                <div
                  className={cn(
                    "mt-0.5 w-px flex-1 bg-border",
                    step.status === "running" && "bg-accent/30",
                  )}
                  style={{ minHeight: 12 }}
                />
              )}
            </div>

            {/* Content */}
            <div
              className={cn(
                "mb-1 min-w-0 flex-1 rounded-lg px-2.5 py-1.5",
                cfg.bg,
              )}
            >
              <p className={cn("text-[13px]", cfg.labelColor)}>{step.label}</p>
              {step.detail && (
                <p className={cn("mt-0.5 text-[11px]", cfg.detailColor)}>
                  {step.detail}
                </p>
              )}
            </div>
          </div>
        );
      })}

      {widget.deepLink && onDeepLink && (
        <button
          onClick={() => onDeepLink(widget.deepLink!)}
          className="mt-1 self-end text-[12px] font-medium text-text-brand hover:underline"
        >
          결과 보기 →
        </button>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function StepProgressSkeleton({ className }: { className?: string }) {
  return <ListSkeleton count={4} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const stepProgressWidget = defineWidget<StepProgressData>({
  type: WIDGET_TYPES.STEP_PROGRESS,
  label: "도구 실행 진행",
  schema: StepProgressSchema,
  component: StepProgress,
  skeleton: StepProgressSkeleton,
  defaultLayout: "compact",
  maxHeight: 200,
  aiHint: "AI 도구 실행 단계별 진행 상태 (pending/running/done/error)",
});

registerWidget(stepProgressWidget);
